import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  Shield, Lock, Eye, Key, Zap, Users, Code2, Building2,
  CheckCircle2, XCircle, ArrowRight, ChevronRight, Activity,
  FileSearch, GitBranch, Terminal, Layers, AlertTriangle,
  Database, Globe, Mail
} from "lucide-react";
import {
  SiSlack, SiGmail, SiNotion, SiGithub, SiSalesforce,
  SiLinear, SiJira, SiAuth0, SiGoogle,
  SiOkta, SiHubspot, SiAirtable
} from "react-icons/si";

// ─── Theme ───────────────────────────────────────────────────────────────────
// Off-white light theme
const T = {
  bg: "#FAFAFA",
  bgAlt: "#FFFFFF",
  surface: "#FFFFFF",
  surfaceMuted: "#F4F4F6",
  border: "rgba(15,23,42,0.07)",
  borderStrong: "rgba(15,23,42,0.12)",
  text: "#0F172A",
  textSecondary: "#1E293B",
  textMuted: "#64748B",
  textFaint: "#94A3B8",
  accent: "#6366F1",
  accentBg: "rgba(99,102,241,0.08)",
  accentBorder: "rgba(99,102,241,0.25)",
};

// ─── Data ────────────────────────────────────────────────────────────────────

const PROBLEMS = [
  "They inherit admin-level permissions by default",
  "They leak sensitive data across tool boundaries",
  "They invoke APIs the user never intended",
  "They act on behalf of users without their knowledge",
  "They leave no audit trail when something goes wrong",
];

const FEATURES = [
  {
    icon: Key,
    title: "Per-User Identity Binding",
    desc: "Every agent action is scoped to the human behind it. No agent can act beyond its user's access level.",
    accent: "from-indigo-50 to-violet-50",
    border: "rgba(99,102,241,0.2)",
    iconBg: "rgba(99,102,241,0.08)",
    iconColor: "text-indigo-500",
    size: "large",
  },
  {
    icon: Lock,
    title: "Fine-Grained Permissions",
    desc: "Define exactly which tool, scope, and action each agent is allowed per employee.",
    accent: "from-blue-50 to-cyan-50",
    border: "rgba(59,130,246,0.2)",
    iconBg: "rgba(59,130,246,0.08)",
    iconColor: "text-blue-500",
    size: "standard",
  },
  {
    icon: Zap,
    title: "Real-Time Guardrails",
    desc: "Every action is inspected and validated before execution. Unsafe calls are blocked instantly.",
    accent: "from-amber-50 to-orange-50",
    border: "rgba(245,158,11,0.2)",
    iconBg: "rgba(245,158,11,0.08)",
    iconColor: "text-amber-500",
    size: "standard",
  },
  {
    icon: FileSearch,
    title: "Full Audit Log",
    desc: "Every agent action logged, queryable, and exportable. Know exactly what happened and when.",
    accent: "from-emerald-50 to-teal-50",
    border: "rgba(16,185,129,0.2)",
    iconBg: "rgba(16,185,129,0.08)",
    iconColor: "text-emerald-500",
    size: "standard",
  },
  {
    icon: Layers,
    title: "Bring Your Own IdP",
    desc: "Plug in Okta, Azure AD, Google Workspace, or any SAML/OIDC provider you already use.",
    accent: "from-rose-50 to-pink-50",
    border: "rgba(244,63,94,0.2)",
    iconBg: "rgba(244,63,94,0.08)",
    iconColor: "text-rose-500",
    size: "standard",
  },
  {
    icon: Terminal,
    title: "TypeScript & Python SDKs",
    desc: "Ship integrations in minutes. Config over custom OAuth. Works with any agent framework.",
    accent: "from-slate-50 to-slate-100",
    border: "rgba(100,116,139,0.2)",
    iconBg: "rgba(100,116,139,0.08)",
    iconColor: "text-slate-500",
    size: "standard",
  },
];

const PERSONAS = [
  {
    icon: Shield,
    role: "IT & Security Teams",
    headline: "Org-wide control. Zero productivity cost.",
    desc: "Define access policies once. Every agent in your org inherits them automatically — no per-integration engineering required.",
    tags: ["SSO", "Audit logs", "Policy enforcement"],
    borderColor: "rgba(99,102,241,0.2)",
    iconBg: "rgba(99,102,241,0.08)",
    iconColor: "text-indigo-500",
  },
  {
    icon: Code2,
    role: "Developers",
    headline: "Auth without the boilerplate.",
    desc: "You can wire up OAuth yourself. But when your agent acts as you instead of your user — and you have no idea what it did or who it did it to — you'll wish you hadn't.",
    subDesc: "Connect once via our SDK and get per-user, scoped access for every agent. No credential juggling, no audit gaps.",
    tags: ["TypeScript SDK", "Python SDK", "Config-based"],
    borderColor: "rgba(59,130,246,0.2)",
    iconBg: "rgba(59,130,246,0.08)",
    iconColor: "text-blue-500",
  },
  {
    icon: Building2,
    role: "Business Leaders",
    headline: "AI at scale. Risk contained.",
    desc: "Deploy agents across your entire org knowing that no employee — and no agent — can ever exceed their intended permissions.",
    tags: ["Compliance-ready", "Full visibility", "Zero trust"],
    borderColor: "rgba(16,185,129,0.2)",
    iconBg: "rgba(16,185,129,0.08)",
    iconColor: "text-emerald-500",
  },
];

const VALUE_PROPS = [
  {
    stat: "0",
    unit: "ms",
    label: "Guardrail latency",
    desc: "Real-time inspection before any tool call executes. Blocking happens synchronously, not after the fact.",
    icon: Zap,
    iconColor: "text-amber-500",
    iconBg: "rgba(245,158,11,0.08)",
    statColor: "#0F172A",
  },
  {
    stat: "100%",
    unit: "",
    label: "Action coverage",
    desc: "Every tool call, every agent, every employee. Nothing passes through without an identity check.",
    icon: Eye,
    iconColor: "text-indigo-500",
    iconBg: "rgba(99,102,241,0.08)",
    statColor: "#0F172A",
  },
  {
    stat: "<5",
    unit: "min",
    label: "Integration time",
    desc: "TypeScript and Python SDKs. Config over custom OAuth. Connect a new tool in minutes, not weeks.",
    icon: GitBranch,
    iconColor: "text-blue-500",
    iconBg: "rgba(59,130,246,0.08)",
    statColor: "#0F172A",
  },
  {
    stat: "∞",
    unit: "",
    label: "Audit depth",
    desc: "Every action is logged, timestamped, and attributed to a user. Full queryable history, always.",
    icon: Database,
    iconColor: "text-emerald-500",
    iconBg: "rgba(16,185,129,0.08)",
    statColor: "#0F172A",
  },
];

const INTEGRATIONS = [
  { Icon: SiSlack, name: "Slack" },
  { Icon: SiGmail, name: "Gmail" },
  { Icon: SiNotion, name: "Notion" },
  { Icon: SiGithub, name: "GitHub" },
  { Icon: SiSalesforce, name: "Salesforce" },
  { Icon: SiLinear, name: "Linear" },
  { Icon: SiJira, name: "Jira" },
  { Icon: SiAuth0, name: "Auth0" },
  { Icon: SiGoogle, name: "Google" },
  { Icon: SiOkta, name: "Okta" },
  { Icon: SiHubspot, name: "HubSpot" },
  { Icon: SiAirtable, name: "Airtable" },
];

const DEMO_SEQUENCE = [
  { agent: "SummaryAgent", action: "Read emails", tool: "Gmail", scope: "mail.readonly", allowed: true, user: "alice@acme.com" },
  { agent: "SalesAgent", action: "Update opportunity", tool: "Salesforce", scope: "opportunity.write", allowed: true, user: "bob@acme.com" },
  { agent: "DataAgent", action: "Delete all records", tool: "Salesforce", scope: "data.delete", allowed: false, user: "carol@acme.com" },
  { agent: "NotionAgent", action: "Read workspace", tool: "Notion", scope: "pages.read", allowed: true, user: "alice@acme.com" },
  { agent: "SlackAgent", action: "Post to #general", tool: "Slack", scope: "channels.write", allowed: false, user: "dave@acme.com" },
];

// ─── Micro Components ─────────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
};

function SectionLabel({ children }) {
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium tracking-widest uppercase border"
      style={{ color: T.accent, borderColor: T.accentBorder, background: T.accentBg, fontFamily: "Manrope, sans-serif" }}>
      {children}
    </span>
  );
}

function PillTag({ children }) {
  return (
    <span className="px-2 py-0.5 rounded text-xs font-medium"
      style={{ background: T.surfaceMuted, color: T.textMuted, border: `1px solid ${T.border}`, fontFamily: "JetBrains Mono, monospace" }}>
      {children}
    </span>
  );
}

// ─── Hero Demo ────────────────────────────────────────────────────────────────

function AccessControlDemo() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [phase, setPhase] = useState("routing");

  useEffect(() => {
    const sequence = async () => {
      setPhase("routing");
      await delay(800);
      setPhase("checking");
      await delay(900);
      setPhase("result");
      await delay(1800);
    };
    sequence().then(() => {
      setTimeout(() => {
        setActiveIdx((i) => (i + 1) % DEMO_SEQUENCE.length);
      }, 400);
    });
  }, [activeIdx]);

  const current = DEMO_SEQUENCE[activeIdx];

  return (
    <div className="relative rounded-2xl overflow-hidden"
      style={{ background: T.surface, border: `1px solid ${T.border}`, boxShadow: "0 4px 24px rgba(15,23,42,0.06)" }}>
      {/* Terminal bar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: T.border, background: T.surfaceMuted }}>
        <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 text-xs" style={{ color: T.textFaint, fontFamily: "JetBrains Mono, monospace" }}>agent-control / access-gateway</span>
      </div>

      <div className="p-6 space-y-4" style={{ minHeight: 260 }}>
        {/* Flow diagram */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Agent */}
          <motion.div key={`agent-${activeIdx}`}
            initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
            className="flex flex-col px-4 py-3 rounded-xl"
            style={{ background: T.accentBg, border: `1px solid ${T.accentBorder}`, minWidth: 140 }}>
            <span className="text-xs mb-1" style={{ color: T.accent, fontFamily: "JetBrains Mono, monospace" }}>AGENT</span>
            <span className="text-sm font-semibold" style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>{current.agent}</span>
            <span className="text-xs mt-1" style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif" }}>{current.user}</span>
          </motion.div>

          {/* Arrow */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: phase !== "routing" ? 1 : 0.3 }} transition={{ duration: 0.3 }}>
            <ArrowRight size={16} style={{ color: T.textFaint }} />
          </motion.div>

          {/* Agent Control node */}
          <motion.div
            animate={{
              borderColor: phase === "checking" ? T.accent : T.accentBorder,
              boxShadow: phase === "checking" ? "0 0 20px rgba(99,102,241,0.15)" : "none",
            }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-center px-4 py-3 rounded-xl"
            style={{ background: "#EEF2FF", border: `1px solid ${T.accentBorder}`, minWidth: 120 }}>
            <Shield size={16} style={{ color: T.accent }} className="mb-1" />
            <span className="text-xs font-bold tracking-wide" style={{ color: T.accent, fontFamily: "Outfit, sans-serif" }}>Agent Control</span>
            {phase === "checking" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-0.5 mt-2">
                {[0, 1, 2].map(i => (
                  <motion.div key={i} className="w-1 h-1 rounded-full" style={{ background: T.accent }}
                    animate={{ opacity: [0.3, 1, 0.3] }} transition={{ repeat: Infinity, duration: 0.8, delay: i * 0.15 }} />
                ))}
              </motion.div>
            )}
          </motion.div>

          {/* Arrow */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: phase === "result" ? 1 : 0.3 }} transition={{ duration: 0.3 }}>
            <ArrowRight size={16} style={{ color: T.textFaint }} />
          </motion.div>

          {/* Tool + result */}
          <motion.div key={`tool-${activeIdx}-${phase}`}
            initial={{ opacity: 0, x: 10 }} animate={{ opacity: phase === "result" ? 1 : 0.4, x: 0 }} transition={{ duration: 0.4 }}
            className="flex flex-col px-4 py-3 rounded-xl"
            style={{
              background: phase === "result" ? (current.allowed ? "rgba(16,185,129,0.06)" : "rgba(239,68,68,0.06)") : T.surfaceMuted,
              border: `1px solid ${phase === "result" ? (current.allowed ? "rgba(16,185,129,0.25)" : "rgba(239,68,68,0.25)") : T.border}`,
              minWidth: 140,
              transition: "background 0.3s, border-color 0.3s",
            }}>
            <span className="text-xs mb-1" style={{ color: T.textMuted, fontFamily: "JetBrains Mono, monospace" }}>TOOL · {current.scope}</span>
            <span className="text-sm font-semibold" style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>{current.tool}</span>
            <span className="text-xs mt-0.5" style={{ color: T.textMuted }}>{current.action}</span>
          </motion.div>

          {/* Verdict badge */}
          <AnimatePresence mode="wait">
            {phase === "result" && (
              <motion.div key="verdict" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                style={{
                  background: current.allowed ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
                  border: `1px solid ${current.allowed ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)"}`,
                }}>
                {current.allowed
                  ? <CheckCircle2 size={14} style={{ color: "#10B981" }} />
                  : <XCircle size={14} style={{ color: "#EF4444" }} />}
                <span className="text-xs font-bold" style={{ color: current.allowed ? "#059669" : "#DC2626", fontFamily: "JetBrains Mono, monospace" }}>
                  {current.allowed ? "ALLOWED" : "BLOCKED"}
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Log lines */}
        <div className="space-y-1.5 pt-2">
          <AnimatePresence>
            {phase !== "routing" && (
              <motion.div key="log1" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2">
                <span style={{ color: T.textFaint, fontFamily: "JetBrains Mono, monospace", fontSize: 11 }}>→</span>
                <span style={{ color: T.textMuted, fontFamily: "JetBrains Mono, monospace", fontSize: 11 }}>
                  Checking identity: <span style={{ color: T.textSecondary }}>{current.user}</span>
                </span>
              </motion.div>
            )}
            {phase === "result" && (
              <>
                <motion.div key="log2" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="flex items-center gap-2">
                  <span style={{ color: T.textFaint, fontFamily: "JetBrains Mono, monospace", fontSize: 11 }}>→</span>
                  <span style={{ color: T.textMuted, fontFamily: "JetBrains Mono, monospace", fontSize: 11 }}>
                    Scope requested: <span style={{ color: T.textSecondary }}>{current.scope}</span>
                  </span>
                </motion.div>
                <motion.div key="log3" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="flex items-center gap-2">
                  <span style={{ color: current.allowed ? "#059669" : "#DC2626", fontFamily: "JetBrains Mono, monospace", fontSize: 11 }}>
                    {current.allowed ? "✓" : "✗"}
                  </span>
                  <span style={{ color: current.allowed ? "#059669" : "#DC2626", fontFamily: "JetBrains Mono, monospace", fontSize: 11 }}>
                    {current.allowed ? `Access granted — forwarding to ${current.tool}` : `Policy violation — ${current.tool} ${current.scope} not permitted for this agent`}
                  </span>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Progress dots */}
      <div className="flex items-center justify-between px-6 py-3 border-t" style={{ borderColor: T.border, background: T.surfaceMuted }}>
        <div className="flex gap-1.5">
          {DEMO_SEQUENCE.map((_, i) => (
            <div key={i} className="w-1.5 h-1.5 rounded-full transition-colors duration-300"
              style={{ background: i === activeIdx ? T.accent : "rgba(15,23,42,0.15)" }} />
          ))}
        </div>
        <span className="text-xs" style={{ color: T.textFaint, fontFamily: "JetBrains Mono, monospace" }}>live demo</span>
      </div>
    </div>
  );
}

// ─── Sections ─────────────────────────────────────────────────────────────────

function Navbar({ onCta }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.nav initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? "rgba(250,250,250,0.92)" : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        borderBottom: scrolled ? `1px solid ${T.border}` : "1px solid transparent",
      }}>
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: T.accentBg, border: `1px solid ${T.accentBorder}` }}>
            <Shield size={14} style={{ color: T.accent }} />
          </div>
          <span className="text-base font-bold" style={{ color: T.text, fontFamily: "Outfit, sans-serif", letterSpacing: "-0.02em" }}>
            Agent Control
          </span>
        </div>

        <div className="hidden md:flex items-center gap-8">
          {[["problem", "Problem"], ["how-it-works", "How It Works"], ["features", "Features"], ["for-who", "Who It's For"]].map(([id, label]) => (
            <button key={id} onClick={() => scrollTo(id)}
              className="text-sm transition-colors duration-200 hover:text-slate-900"
              style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif", background: "none", border: "none", cursor: "pointer" }}>
              {label}
            </button>
          ))}
        </div>

        <button onClick={onCta} data-testid="nav-cta"
          className="flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 hover:shadow-md active:scale-[0.97]"
          style={{ background: T.accent, color: "#fff", fontFamily: "Manrope, sans-serif", boxShadow: "0 2px 12px rgba(99,102,241,0.25)" }}>
          Get early access
          <ChevronRight size={14} />
        </button>
      </div>
    </motion.nav>
  );
}

function HeroSection({ onCta }) {
  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-16"
      style={{ background: T.bg }}>
      {/* Ambient decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[350px] rounded-full opacity-30"
          style={{ background: "radial-gradient(ellipse, rgba(99,102,241,0.12) 0%, transparent 70%)", filter: "blur(60px)" }} />
        <div className="absolute top-0 left-0 right-0 h-px"
          style={{ background: "linear-gradient(to right, transparent, rgba(99,102,241,0.2), transparent)" }} />
        {/* Dot grid */}
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(circle, rgba(99,102,241,0.12) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          maskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black 40%, transparent 100%)",
        }} />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <SectionLabel>Identity & Access for AI Agents</SectionLabel>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 text-5xl md:text-7xl font-bold leading-[1.05] tracking-tight"
          style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>
          Your agents are acting<br />
          <span style={{ color: T.accent }}>on your behalf.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.3 }}
          className="mt-6 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
          style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif" }}>
          Agent Control is the identity and access management layer for AI agents — enforcing permissions, scoping tool access, and blocking unsafe actions before they execute.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.45 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button onClick={onCta} data-testid="hero-cta-primary"
            className="flex items-center gap-2 px-8 py-3.5 rounded-full text-base font-semibold transition-all duration-200 hover:shadow-lg active:scale-[0.97]"
            style={{ background: T.accent, color: "#fff", fontFamily: "Manrope, sans-serif", boxShadow: "0 4px 20px rgba(99,102,241,0.3)" }}>
            Get early access
            <ArrowRight size={16} />
          </button>
          <button onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-cta-secondary"
            className="flex items-center gap-2 px-8 py-3.5 rounded-full text-base font-medium transition-all duration-200 hover:border-slate-400"
            style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif", border: `1px solid ${T.borderStrong}`, background: "transparent" }}>
            See how it works
            <ChevronRight size={16} />
          </button>
        </motion.div>

        {/* Demo */}
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.6 }}
          className="mt-16 max-w-3xl mx-auto">
          <AccessControlDemo />
        </motion.div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
        style={{ background: `linear-gradient(to bottom, transparent, ${T.bg})` }} />
    </section>
  );
}

function ProblemSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="problem" ref={ref} className="py-28 relative overflow-hidden"
      style={{ background: T.bgAlt, borderTop: `1px solid ${T.border}` }}>
      <div className="max-w-6xl mx-auto px-6">
        <motion.div variants={stagger} initial="hidden" animate={inView ? "visible" : "hidden"} className="flex flex-col lg:flex-row gap-16 items-center">
          {/* Left — stat */}
          <motion.div variants={fadeUp} className="flex-shrink-0 lg:w-72 text-center lg:text-left">
            <div className="inline-block">
              <span className="block text-8xl font-bold leading-none"
                style={{ color: "#EF4444", fontFamily: "Outfit, sans-serif" }}>
                88%
              </span>
              <span className="block mt-3 text-base leading-snug" style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif" }}>
                of companies have already experienced an AI agent incident
              </span>
            </div>
          </motion.div>

          {/* Divider */}
          <div className="hidden lg:block w-px self-stretch" style={{ background: T.border }} />

          {/* Right — problems */}
          <div className="flex-1">
            <motion.div variants={fadeUp} className="mb-8">
              <SectionLabel>The Problem</SectionLabel>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold leading-tight"
                style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>
                Agents don't know their limits.
              </h2>
            </motion.div>

            <motion.ul variants={stagger} className="space-y-3">
              {PROBLEMS.map((p, i) => (
                <motion.li key={i} variants={fadeUp}
                  className="flex items-start gap-4 px-5 py-4 rounded-xl"
                  style={{ background: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.12)" }}>
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" style={{ color: "#EF4444" }} />
                  <span className="text-sm leading-relaxed" style={{ color: T.textSecondary, fontFamily: "Manrope, sans-serif" }}>{p}</span>
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const adminSteps = [
    { n: "01", title: "Register your tools", desc: "Pick from 50+ pre-built integrations or connect any OAuth service." },
    { n: "02", title: "Define agent policies", desc: "Set which scopes each agent is allowed per employee role or group." },
    { n: "03", title: "Add your employees", desc: "Invite team members and set their personal tool connection permissions." },
    { n: "04", title: "Monitor everything", desc: "Full real-time audit of every agent action across your entire org." },
  ];

  const userSteps = [
    { n: "01", title: "Connect your account", desc: "Link your personal Slack, Gmail, Notion — within your admin's policy." },
    { n: "02", title: "Use agents normally", desc: "Your tools are available to agents — scoped only to what you're allowed." },
  ];

  return (
    <section id="how-it-works" ref={ref} className="py-28"
      style={{ background: T.bg, borderTop: `1px solid ${T.border}` }}>
      <div className="max-w-6xl mx-auto px-6">
        <motion.div variants={fadeUp} initial="hidden" animate={inView ? "visible" : "hidden"} className="text-center mb-16">
          <SectionLabel>How It Works</SectionLabel>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold" style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>
            The secure middle layer.
          </h2>
          <p className="mt-4 text-base max-w-xl mx-auto" style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif" }}>
            Every agent action flows through Agent Control — enforcing identity, permissions, and guardrails before anything executes.
          </p>
        </motion.div>

        {/* Flow diagram */}
        <motion.div variants={fadeUp} initial="hidden" animate={inView ? "visible" : "hidden"}
          className="flex items-center justify-center gap-3 mb-8 flex-wrap">
          {[
            { label: "Your Agent", sub: "Any framework", color: T.accentBg, border: T.accentBorder },
            null,
            { label: "Agent Control", sub: "Identity · Permissions · Guardrails", color: "#EEF2FF", border: T.accent, highlight: true },
            null,
            { label: "Tool / API", sub: "Slack, Gmail, Salesforce…", color: "rgba(16,185,129,0.06)", border: "rgba(16,185,129,0.25)" },
          ].map((node, i) =>
            node === null ? (
              <ArrowRight key={i} size={20} style={{ color: T.textFaint }} />
            ) : (
              <div key={i} className="flex flex-col items-center px-6 py-4 rounded-2xl"
                style={{ background: node.color, border: `1px solid ${node.border}`, boxShadow: node.highlight ? "0 4px 20px rgba(99,102,241,0.12)" : "none", minWidth: 160 }}>
                {node.highlight && <Shield size={18} className="mb-2" style={{ color: T.accent }} />}
                <span className="text-sm font-bold" style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>{node.label}</span>
                <span className="text-xs mt-1 text-center" style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif" }}>{node.sub}</span>
              </div>
            )
          )}
        </motion.div>

        {/* Enforcement mechanic callout */}
        <motion.div variants={fadeUp} initial="hidden" animate={inView ? "visible" : "hidden"}
          className="mb-16 mx-auto max-w-2xl px-6 py-5 rounded-2xl flex items-start gap-4"
          style={{ background: T.accentBg, border: `1px solid ${T.accentBorder}` }}>
          <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: "#EEF2FF", border: `1px solid ${T.accentBorder}` }}>
            <Lock size={15} style={{ color: T.accent }} />
          </div>
          <div>
            <p className="text-sm font-semibold mb-1" style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>
              Your tool credentials live in Agent Control — not in your agents.
            </p>
            <p className="text-xs leading-relaxed" style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif" }}>
              OAuth tokens for Slack, Gmail, Salesforce and every other tool are vaulted here. Agents never hold credentials directly — they request scoped access through the SDK. There's nothing to bypass.
            </p>
          </div>
        </motion.div>

        {/* Two-column POV */}
        <motion.div variants={stagger} initial="hidden" animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 gap-8">
          {/* Admin POV */}
          <motion.div variants={fadeUp} className="p-8 rounded-2xl"
            style={{ background: T.surface, border: `1px solid ${T.border}`, boxShadow: "0 2px 12px rgba(15,23,42,0.04)" }}>
            <div className="flex items-center gap-3 mb-7">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: T.accentBg, border: `1px solid ${T.accentBorder}` }}>
                <Shield size={15} style={{ color: T.accent }} />
              </div>
              <div>
                <span className="text-xs tracking-widest uppercase" style={{ color: T.accent, fontFamily: "Manrope, sans-serif" }}>Admin / IT</span>
                <p className="text-sm font-semibold" style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>Set it up once, enforce it everywhere</p>
              </div>
            </div>
            <div className="space-y-5">
              {adminSteps.map((s) => (
                <div key={s.n} className="flex gap-4">
                  <span className="text-xs font-bold flex-shrink-0 mt-0.5" style={{ color: T.textFaint, fontFamily: "JetBrains Mono, monospace" }}>{s.n}</span>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>{s.title}</p>
                    <p className="text-xs mt-1 leading-relaxed" style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif" }}>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* User POV */}
          <motion.div variants={fadeUp} className="p-8 rounded-2xl"
            style={{ background: T.surface, border: `1px solid ${T.border}`, boxShadow: "0 2px 12px rgba(15,23,42,0.04)" }}>
            <div className="flex items-center gap-3 mb-7">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)" }}>
                <Users size={15} style={{ color: "#10B981" }} />
              </div>
              <div>
                <span className="text-xs tracking-widest uppercase" style={{ color: "#059669", fontFamily: "Manrope, sans-serif" }}>Employee / End User</span>
                <p className="text-sm font-semibold" style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>Use agents within your boundaries</p>
              </div>
            </div>
            <div className="space-y-5">
              {userSteps.map((s) => (
                <div key={s.n} className="flex gap-4">
                  <span className="text-xs font-bold flex-shrink-0 mt-0.5" style={{ color: T.textFaint, fontFamily: "JetBrains Mono, monospace" }}>{s.n}</span>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>{s.title}</p>
                    <p className="text-xs mt-1 leading-relaxed" style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif" }}>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 px-5 py-4 rounded-xl"
              style={{ background: "rgba(16,185,129,0.05)", border: "1px solid rgba(16,185,129,0.15)" }}>
              <p className="text-sm italic leading-relaxed" style={{ color: "#059669", fontFamily: "Manrope, sans-serif" }}>
                "Make your agents act with the user in mind — not with root access."
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const large = FEATURES.filter(f => f.size === "large");
  const standard = FEATURES.filter(f => f.size === "standard");

  return (
    <section id="features" ref={ref} className="py-28"
      style={{ background: T.bgAlt, borderTop: `1px solid ${T.border}` }}>
      <div className="max-w-6xl mx-auto px-6">
        <motion.div variants={fadeUp} initial="hidden" animate={inView ? "visible" : "hidden"} className="mb-14">
          <SectionLabel>Features</SectionLabel>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold" style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>
            Built for enterprise-grade<br />agent security.
          </h2>
        </motion.div>

        <motion.div variants={stagger} initial="hidden" animate={inView ? "visible" : "hidden"} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Large card spans 2 cols */}
          {large.map(f => (
            <motion.div key={f.title} variants={fadeUp}
              className={`md:col-span-2 p-8 rounded-2xl bg-gradient-to-br ${f.accent} transition-all duration-300 hover:shadow-md`}
              style={{ border: `1px solid ${f.border}` }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                style={{ background: f.iconBg, border: `1px solid ${f.border}` }}>
                <f.icon size={20} className={f.iconColor} />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>{f.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif" }}>{f.desc}</p>
            </motion.div>
          ))}

          {/* Standard cards */}
          {standard.map(f => (
            <motion.div key={f.title} variants={fadeUp}
              className={`p-6 rounded-2xl bg-gradient-to-br ${f.accent} transition-all duration-300 hover:shadow-md`}
              style={{ border: `1px solid ${f.border}` }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-4"
                style={{ background: f.iconBg, border: `1px solid ${f.border}` }}>
                <f.icon size={17} className={f.iconColor} />
              </div>
              <h3 className="text-base font-bold mb-2" style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>{f.title}</h3>
              <p className="text-xs leading-relaxed" style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif" }}>{f.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ForWhoSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="for-who" ref={ref} className="py-28"
      style={{ background: T.bg, borderTop: `1px solid ${T.border}` }}>
      <div className="max-w-6xl mx-auto px-6">
        <motion.div variants={fadeUp} initial="hidden" animate={inView ? "visible" : "hidden"} className="text-center mb-14">
          <SectionLabel>Who It's For</SectionLabel>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold" style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>
            Built for everyone<br />who ships agents.
          </h2>
        </motion.div>

        <motion.div variants={stagger} initial="hidden" animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-3 gap-6">
          {PERSONAS.map((p) => (
            <motion.div key={p.role} variants={fadeUp}
              className="p-7 rounded-2xl flex flex-col gap-5 transition-all duration-300 hover:shadow-md"
              style={{ background: T.surface, border: `1px solid ${p.borderColor}`, boxShadow: "0 1px 8px rgba(15,23,42,0.04)" }}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ background: p.iconBg, border: `1px solid ${p.borderColor}` }}>
                <p.icon size={20} className={p.iconColor} />
              </div>
              <div>
                <p className="text-xs tracking-widest uppercase mb-1" style={{ color: T.textFaint, fontFamily: "Manrope, sans-serif" }}>{p.role}</p>
                <h3 className="text-lg font-bold leading-snug" style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>{p.headline}</h3>
              </div>
              <div className="flex-1 space-y-2">
                <p className="text-sm leading-relaxed" style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif" }}>{p.desc}</p>
                {p.subDesc && <p className="text-xs leading-relaxed" style={{ color: T.textFaint, fontFamily: "Manrope, sans-serif" }}>{p.subDesc}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                {p.tags.map(t => <PillTag key={t}>{t}</PillTag>)}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ValuePropsSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section ref={ref} className="py-28"
      style={{ background: T.bgAlt, borderTop: `1px solid ${T.border}` }}>
      <div className="max-w-6xl mx-auto px-6">
        <motion.div variants={fadeUp} initial="hidden" animate={inView ? "visible" : "hidden"} className="text-center mb-14">
          <SectionLabel>Why It Matters</SectionLabel>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold" style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>
            Real outcomes. Not just promises.
          </h2>
        </motion.div>

        <motion.div variants={stagger} initial="hidden" animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 gap-5">
          {VALUE_PROPS.map((v) => (
            <motion.div key={v.label} variants={fadeUp}
              className="flex gap-6 p-7 rounded-2xl transition-all duration-300 hover:shadow-md"
              style={{ background: T.surface, border: `1px solid ${T.border}`, boxShadow: "0 1px 8px rgba(15,23,42,0.04)" }}>
              <div className="flex-shrink-0 pt-1">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: v.iconBg, border: `1px solid ${T.border}` }}>
                  <v.icon size={18} className={v.iconColor} />
                </div>
              </div>
              <div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-bold leading-none" style={{ color: v.statColor, fontFamily: "Outfit, sans-serif" }}>{v.stat}</span>
                  {v.unit && <span className="text-sm font-medium" style={{ color: T.textMuted, fontFamily: "JetBrains Mono, monospace" }}>{v.unit}</span>}
                </div>
                <p className="text-sm font-semibold mb-2" style={{ color: T.textSecondary, fontFamily: "Manrope, sans-serif" }}>{v.label}</p>
                <p className="text-xs leading-relaxed" style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif" }}>{v.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function IntegrationRail() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const doubled = [...INTEGRATIONS, ...INTEGRATIONS];

  return (
    <section ref={ref} className="py-20 overflow-hidden"
      style={{ background: T.bg, borderTop: `1px solid ${T.border}` }}>
      <div className="max-w-6xl mx-auto px-6">
        <motion.p variants={fadeUp} initial="hidden" animate={inView ? "visible" : "hidden"}
          className="text-center text-xs tracking-widest uppercase mb-10"
          style={{ color: T.textFaint, fontFamily: "Manrope, sans-serif" }}>
          Works with the tools your teams already use
        </motion.p>
      </div>

      {/* Scrolling rail */}
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
          style={{ background: `linear-gradient(to right, ${T.bg}, transparent)` }} />
        <div className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
          style={{ background: `linear-gradient(to left, ${T.bg}, transparent)` }} />

        <motion.div className="flex gap-4 w-max"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}>
          {doubled.map(({ Icon, name }, i) => (
            <div key={`${name}-${i}`} className="flex items-center gap-3 px-5 py-3 rounded-xl flex-shrink-0"
              style={{ background: T.surface, border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.04)" }}>
              <Icon size={18} style={{ color: T.textMuted }} />
              <span className="text-sm font-medium" style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif" }}>{name}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function CtaSection({ emailRef, email, setEmail, submitted, onSubmit }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section ref={ref} className="py-32 relative overflow-hidden"
      style={{ background: T.bgAlt, borderTop: `1px solid ${T.border}` }}>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[500px] h-[250px]"
          style={{ background: "radial-gradient(ellipse, rgba(99,102,241,0.08) 0%, transparent 70%)", filter: "blur(40px)" }} />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
        <motion.div variants={stagger} initial="hidden" animate={inView ? "visible" : "hidden"}>
          <motion.div variants={fadeUp}><SectionLabel>Early Access</SectionLabel></motion.div>

          <motion.h2 variants={fadeUp} className="mt-6 text-4xl md:text-6xl font-bold leading-tight"
            style={{ color: T.text, fontFamily: "Outfit, sans-serif" }}>
            Your agents are already running.
          </motion.h2>

          <motion.p variants={fadeUp} className="mt-5 text-base leading-relaxed"
            style={{ color: T.textMuted, fontFamily: "Manrope, sans-serif" }}>
            Time to put them under control. Join the early access list and be the first to secure your agentic stack.
          </motion.p>

          <motion.div variants={fadeUp} ref={emailRef} className="mt-10">
            {submitted ? (
              <div className="flex items-center justify-center gap-3 px-6 py-4 rounded-2xl"
                style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)" }}>
                <CheckCircle2 size={20} style={{ color: "#059669" }} />
                <span className="text-base font-semibold" style={{ color: "#059669", fontFamily: "Manrope, sans-serif" }}>
                  You're on the list. We'll be in touch.
                </span>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="cta-email-input"
                  className="flex-1 px-5 py-3.5 rounded-full text-sm outline-none placeholder:text-slate-400"
                  style={{
                    background: T.surface,
                    border: `1px solid ${T.borderStrong}`,
                    color: T.text,
                    fontFamily: "Manrope, sans-serif",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={e => { e.target.style.borderColor = T.accent; }}
                  onBlur={e => { e.target.style.borderColor = T.borderStrong; }}
                />
                <button type="submit" data-testid="cta-submit"
                  className="flex-shrink-0 flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold transition-all duration-200 hover:shadow-md active:scale-[0.97]"
                  style={{ background: T.accent, color: "#fff", fontFamily: "Manrope, sans-serif", boxShadow: "0 2px 12px rgba(99,102,241,0.25)" }}>
                  Get early access
                  <ArrowRight size={15} />
                </button>
              </form>
            )}
          </motion.div>

          <motion.p variants={fadeUp} className="mt-5 text-xs" style={{ color: T.textFaint, fontFamily: "Manrope, sans-serif" }}>
            No spam. No credit card. Just early access.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-10 px-6" style={{ background: T.surfaceMuted, borderTop: `1px solid ${T.border}` }}>
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: T.accentBg, border: `1px solid ${T.accentBorder}` }}>
            <Shield size={12} style={{ color: T.accent }} />
          </div>
          <span className="text-sm font-semibold" style={{ color: T.textSecondary, fontFamily: "Outfit, sans-serif" }}>Agent Control</span>
        </div>
        <p className="text-xs" style={{ color: T.textFaint, fontFamily: "Manrope, sans-serif" }}>
          © 2026 Agent Control. Okta for your agents.
        </p>
        <div className="flex gap-6">
          {["Privacy", "Terms", "Security"].map(l => (
            <a key={l} href="#" className="text-xs transition-colors duration-200 hover:text-slate-700"
              style={{ color: T.textFaint, fontFamily: "Manrope, sans-serif", textDecoration: "none" }}>
              {l}
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function delay(ms) {
  return new Promise(res => setTimeout(res, ms));
}

export default function AgentControlLandingPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const emailRef = useRef(null);

  const scrollToCta = () => {
    emailRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div style={{ background: T.bg, minHeight: "100vh" }}>
      <Navbar onCta={scrollToCta} />
      <HeroSection onCta={scrollToCta} />
      <ProblemSection />
      <HowItWorksSection />
      <FeaturesSection />
      <ForWhoSection />
      <ValuePropsSection />
      <IntegrationRail />
      <CtaSection
        emailRef={emailRef}
        email={email}
        setEmail={setEmail}
        submitted={submitted}
        onSubmit={handleSubmit}
      />
      <Footer />
    </div>
  );
}
